import useSWR from "swr";
import { useSDK } from "@contentful/react-apps-toolkit";
import { FetchProps, Locale, swrKeys } from "../lib";
import { getPlanFromCredentials } from "../utils";
import { LocalesResponse } from "./types";

const DeeplLocalesFetcher = async (
    credentials: string,
    type: "source" | "target"
): Promise<Locale[]> => {
    const plan = getPlanFromCredentials(credentials);

    const response = await fetch(`/api/${plan}/v2/languages?type=${type}`, {
        method: "GET",
        headers: {
            Authorization: `DeepL-Auth-Key ${credentials}`,
            Connection: "keep-alive",
        },
    });

    if (!response.ok) {
        throw new Error(`Error fetching DeepL ${type} languages. Status: ${response.status}`);
    }

    const data: LocalesResponse[] = await response.json();

    return data.map(locale => locale.language);
};

interface useFetchDeeplLocalesProps {
    source: FetchProps;
    target: FetchProps;
}

export const useFetchDeeplLocales = (props?: useFetchDeeplLocalesProps) => {
    const { source, target } = props ?? {};
    const sdk = useSDK();
    const deeplCredentials = sdk.parameters.installation.deeplApiKey;

    const { data: deeplSourceLocales, isLoading: isDeeplSourceLocalesLoading } = useSWR(
        [swrKeys.deeplSourceLocales, deeplCredentials, !!source?.skip],
        () => (!source?.skip ? DeeplLocalesFetcher(deeplCredentials, "source") : undefined)
    );

    const { data: deeplTargetLocales, isLoading: isDeeplTargetLocalesLoading } = useSWR(
        [swrKeys.deeplTargetLocales, deeplCredentials, !!target?.skip],
        () => (!target?.skip ? DeeplLocalesFetcher(deeplCredentials, "target") : undefined)
    );

    return {
        deeplSourceLocales,
        deeplTargetLocales,
        isDeeplSourceLocalesLoading,
        isDeeplTargetLocalesLoading,
    };
};
